import api from '../../../api/client';
import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Select,
  DatePicker,
  Space,
  Card,
  Spin,
  message,
  Typography,
  Input,
  Divider,
} from "antd";
import {
  ReloadOutlined,
  ClearOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
  BackwardFilled,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { saveAs } from "file-saver";
import { formatKES } from "../../../utils/currency";
import config from "../../../config";
import { useParams } from "react-router-dom";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const GeneralLedgerReport = () => {
  const { account_code, account_name } = useParams();
  const today = dayjs();
  const defaultStartDate = today.subtract(3, "month").format("YYYY-MM-DD");

  const [filters, setFilters] = useState({
    startDate: defaultStartDate,
    endDate: today.format("YYYY-MM-DD"),
    account: account_code,
    farmId: "",
    farmName: "",
  });
  const [reportBasis, setReportBasis] = useState("accrual");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [farms, setFarms] = useState([]);
  const [searchText, setSearchText] = useState("");

  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch farms
  const fetchFarms = useCallback(async () => {
    try {
      const { data } = await api.get(`/farm`);
      setFarms(data);
    } catch (error) {
      message.error("Failed to fetch farms");
    }
  }, [apiUrl]);

  // Fetch account statement
  const fetchAccountStatement = useCallback(async () => {
    if (!account_code) return;
    setLoading(true);
    try {
      const params = {
        account: account_code,
        startDate: filters.startDate,
        endDate: filters.endDate,
        farmId: filters.farmId,
      };
      const { data } = await api.get(
        `/api/reports/account-statement`,
        { params },
      );
      setData(data.transactions || []);
      setOpeningBalance(data.openingBalance || 0);
      setClosingBalance(data.closingBalance || 0);
    } catch (error) {
      message.error("Failed to fetch account statement");
    } finally {
      setLoading(false);
    }
  }, [account_code, filters, apiUrl]);

  useEffect(() => {
    fetchFarms();
  }, [fetchFarms]);

  useEffect(() => {
    fetchAccountStatement();
  }, [fetchAccountStatement]);

  // Filter transactions based on search text
  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    return (
      item.date?.toLowerCase().includes(term) ||
      item.code?.toLowerCase().includes(term) ||
      item.description?.toLowerCase().includes(term) ||
      item.transaction_type?.toLowerCase().includes(term)
    );
  });

  const handleClearFilters = () => {
    setFilters({
      startDate: defaultStartDate,
      endDate: today.format("YYYY-MM-DD"),
      account: account_code,
      farmId: "",
      farmName: "",
    });
    setReportBasis("accrual");
    setSearchText("");
  };

  // Export CSV
  const exportToCSV = () => {
    if (filteredData.length === 0) {
      message.warning("No data to export");
      return;
    }
    setExporting(true);
    try {
      const headers = [
        "Date",
        "Reference",
        "Description",
        "Type",
        "Debit (KES)",
        "Credit (KES)",
      ];
      const rows = filteredData.map((item) => [
        item.date,
        item.code,
        item.description,
        item.transaction_type,
        item.debit,
        item.credit,
      ]);
      const csvContent = [headers, ...rows]
        .map((row) => row.join(","))
        .join("\n");
      const blob = new Blob(["\ufeff" + csvContent], {
        type: "text/csv;charset=utf-8",
      });
      saveAs(
        blob,
        `account_statement_${dayjs().format("YYYYMMDD_HHmmss")}.csv`,
      );
      message.success("CSV exported");
    } catch (error) {
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Export PDF
  const exportToPDF = () => {
    if (filteredData.length === 0) {
      message.warning("No data to export");
      return;
    }
    setExporting(true);
    try {
      const doc = new jsPDF();
      doc.autoTable({
        head: [
          [
            "Date",
            "Reference",
            "Description",
            "Type",
            "Debit (KES)",
            "Credit (KES)",
          ],
        ],
        body: filteredData.map((item) => [
          item.date,
          item.code,
          item.description,
          item.transaction_type,
          formatKES(item.debit),
          formatKES(item.credit),
        ]),
      });
      doc.save(`account_statement_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);
      message.success("PDF exported");
    } catch (error) {
      message.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD") : ""),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    { title: "Reference", dataIndex: "code", key: "code", sorter: true },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    // { title: "Type", dataIndex: "transaction_type", key: "type" },
    {
      title: "Debit (KES)",
      dataIndex: "debit",
      key: "debit",
      render: (val) => formatKES(val),
      align: "right",
      sorter: (a, b) => a.debit - b.debit,
    },
    {
      title: "Credit (KES)",
      dataIndex: "credit",
      key: "credit",
      render: (val) => formatKES(val),
      align: "right",
      sorter: (a, b) => a.credit - b.credit,
    },
  ];

  const handleBack = () => {
    window.history.back();
  };

  return (
    <>
      <Card
        title="Filters"
        extra={
          <Space>
            <Button
              // type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              Back to GL
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          {/* <Col xs={24} sm={12} md={6}>
            <Form.Item label="Farm">
              <Select
                allowClear
                placeholder="Select farm"
                options={farms.map((farm) => ({
                  label: farm.name,
                  value: farm.id,
                }))}
                value={filters.farmId || null}
                onChange={(val, option) =>
                  setFilters((prev) => ({
                    ...prev,
                    farmId: val,
                    farmName: option?.label,
                  }))
                }
              />
            </Form.Item>
          </Col> */}
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Date Range">
              <RangePicker
                value={[
                  filters.startDate ? dayjs(filters.startDate) : null,
                  filters.endDate ? dayjs(filters.endDate) : null,
                ]}
                onChange={(dates) => {
                  if (!dates) return;
                  setFilters((prev) => ({
                    ...prev,
                    startDate: dates[0].format("YYYY-MM-DD"),
                    endDate: dates[1].format("YYYY-MM-DD"),
                  }));
                }}
                presets={[
                  { label: "Today", value: [dayjs(), dayjs()] },
                  {
                    label: "This Week",
                    value: [dayjs().startOf("week"), dayjs().endOf("week")],
                  },
                  {
                    label: "This Month",
                    value: [dayjs().startOf("month"), dayjs().endOf("month")],
                  },
                  {
                    label: "Last Month",
                    value: [
                      dayjs().subtract(1, "month").startOf("month"),
                      dayjs().subtract(1, "month").endOf("month"),
                    ],
                  },
                  {
                    label: "Last 6 Months",
                    value: [
                      dayjs().subtract(6, "month").startOf("month"),
                      dayjs().subtract(1, "month").endOf("month"),
                    ],
                  },
                  {
                    label: "This Year",
                    value: [dayjs().startOf("year"), dayjs().endOf("year")],
                  },
                  {
                    label: "Last Year",
                    value: [
                      dayjs().subtract(1, "year").startOf("year"),
                      dayjs().subtract(1, "year").endOf("year"),
                    ],
                  },
                ]}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Report Basis">
              <Select
                options={[
                  { label: "Cash Basis", value: "cash" },
                  { label: "Accrual Basis", value: "accrual" },
                ]}
                value={reportBasis}
                onChange={setReportBasis}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={fetchAccountStatement}
                loading={loading}
              >
                Apply Filters
              </Button>
              <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                Clear
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                onClick={exportToCSV}
                disabled={filteredData.length === 0}
                loading={exporting}
              >
                CSV
              </Button>
              <Button
                icon={<FilePdfOutlined />}
                onClick={exportToPDF}
                disabled={filteredData.length === 0}
                loading={exporting}
              >
                PDF
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card
        style={{ marginTop: 24 }}
        title="Account Transaction Statement"
        extra={
          <Space>
            <Text type="secondary">{filters.farmName || "SFARMS"}</Text>
            <Text type="secondary">
              From:{" "}
              {filters.startDate
                ? dayjs(filters.startDate).format("DD/MM/YYYY")
                : "N/A"}{" "}
              To:{" "}
              {filters.endDate
                ? dayjs(filters.endDate).format("DD/MM/YYYY")
                : "N/A"}
            </Text>
            <Text type="secondary" style={{ fontStyle: "italic" }}>
              As of {dayjs().format("DD/MM/YYYY")}
            </Text>
          </Space>
        }
      >
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <Title level={4}>
            {account_name} - {account_code}
          </Title>

          <Text type="secondary">
            Basis: {reportBasis === "cash" ? "Cash Basis" : "Accrual Basis"}
          </Text>
        </div>
        <Divider />

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Text strong>Opening Balance: {formatKES(openingBalance)}</Text>
          </Col>
          <Col xs={24} sm={8}>
            <Text strong>Closing Balance: {formatKES(closingBalance)}</Text>
          </Col>
          <Col xs={24} sm={8} style={{ textAlign: "right" }}>
            <Input.Search
              placeholder="Search by date, ref, description..."
              allowClear
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(val) => setSearchText(val)}
            />
          </Col>
        </Row>
        <Divider />

        <Spin spinning={loading}>
          <Table
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(record, idx) => idx}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              pageSize: 100,
            }}
            scroll={{ x: "max-content" }}
            bordered
          />
        </Spin>
      </Card>
    </>
  );
};

export default GeneralLedgerReport;
